import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import type { DirectedScript } from 'fym-shared/src/music24/directedVideoApi';
import type { MusicVideoSongMapSection } from 'fym-shared/src/music24/musicVideoApi';
import { TypedMemo } from '../../common/types';
import { effectFamilyMessages, effectKindMessages } from '../../musicVideoEditor/messages/effectMessages';
import { formatTime } from '../../musicVideoEditor/timeline/timelineGeometry';
import { scriptMessages as messages } from '../messages/scriptMessages';
import { sectionBarItems, sectionDisplayName, type SectionTone } from '../shell/sectionBar';
import { EFFECT_TONES, effectToneOf, type EffectTone } from './script/scriptEdits';
import {
  sectionCellLabel,
  shotCellLabel,
  STRIP_ZOOMS,
  stripCell,
  stripEffectMark,
  stripTrackWidth,
  type StripZoom,
} from './script/stripLayout';
import { useElementWidth } from './script/useElementWidth';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the script step; each part uses its own slice of it */
import styles from './script/Script.module.scss';
/* eslint-enable css-modules/no-unused-class */

const SECTION_CLASS: Readonly<Record<SectionTone, string>> = {
  intro: styles.secIntro ?? '',
  verse: styles.secVerse ?? '',
  chorus: styles.secChorus ?? '',
  bridge: styles.secBridge ?? '',
  outro: styles.secOutro ?? '',
};

const EFFECT_CLASS: Readonly<Record<EffectTone, string>> = {
  title: styles.fxTitle ?? '',
  transition: styles.fxTransition ?? '',
  lyric: styles.fxLyric ?? '',
  shot: styles.fxShot ?? '',
};

/** One bar of the song lane for each this many pixels. */
const WAVE_BAR_PX = 6;

interface ScriptTimelineProps {
  script: DirectedScript;
  sections: readonly MusicVideoSongMapSection[];
  durationSec: number;
  /** The shot open for edit, or null. */
  selectedShotId: string | null;
  /** A click on a shot scrolls the list to its row. */
  onShot: (shotId: string) => void;
}

const px = (cell: { left: number; width: number }): React.CSSProperties => ({
  left: `${cell.left}px`,
  width: `${cell.width}px`,
});

/**
 * The read-only map of the script (design ScriptStrip, e1): the sections, the shots, the effects
 * in one row per family colour, and the song. A cell shows its label only when the label fits; the
 * tooltip always names it. A long song scrolls sideways, and the zoom makes the cells wider.
 */
const ScriptTimelineComponent: React.FC<ScriptTimelineProps> = ({
  script,
  sections,
  durationSec,
  selectedShotId,
  onShot,
}) => {
  const intl = useIntl();
  const span = Math.max(1, durationSec);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const viewportPx = useElementWidth(scrollerRef);
  const [zoom, setZoom] = useState<StripZoom>(1);
  const trackPx = stripTrackWidth(viewportPx, span, zoom);
  const sectionItems = useMemo(() => sectionBarItems(sections), [sections]);
  const tip = (name: string, from: number, to: number) =>
    intl.formatMessage(messages.stripTip, { name, from: formatTime(from), to: formatTime(to) });

  const tones = useMemo(() => {
    const used = new Set(script.effects.map((effect) => effectToneOf(effect)));
    return EFFECT_TONES.filter((tone) => used.has(tone));
  }, [script.effects]);

  const wave = useMemo(() => {
    const count = Math.max(60, Math.round(trackPx / WAVE_BAR_PX));
    return Array.from({ length: count }, (_, index) => {
      const sec = (index / count) * span;
      const section = sections.find((item) => sec >= item.startSec && sec < item.endSec);
      const energy = section?.energy ?? 0.4;
      const level = Math.max(0.1, energy * (0.55 + 0.45 * Math.abs(Math.sin(index * 1.7) * Math.cos(index * 0.31))));
      const gap = ((1 - level) / 2) * 100;
      return { key: index, style: { left: `${(index / count) * 100}%`, top: `${gap}%`, bottom: `${gap}%` } };
    });
  }, [sections, span, trackPx]);

  // Keep the shot open for edit in view.
  useEffect(() => {
    const scroller = scrollerRef.current;
    const shot = script.shots.find((item) => item.id === selectedShotId);
    if (scroller === null || shot === undefined) {
      return;
    }
    const cell = stripCell(shot.startSec, shot.endSec, span, trackPx);
    const viewLeft = scroller.scrollLeft;
    const viewRight = viewLeft + scroller.clientWidth;
    if (cell.left < viewLeft || cell.left + cell.width > viewRight) {
      scroller.scrollLeft = Math.max(0, cell.left - 48);
    }
  }, [selectedShotId, script.shots, span, trackPx]);

  const zoomIndex = STRIP_ZOOMS.indexOf(zoom);

  return (
    <div className={styles.strip} role="group" aria-label={intl.formatMessage(messages.stripLabel)}>
      <div className={styles.stripHead}>
        {tones.length > 0 && (
          <ul className={styles.legend} aria-label={intl.formatMessage(messages.legendLabel)}>
            {tones.map((tone) => (
              <li key={tone}>
                <i className={clsx(styles.legendSwatch, EFFECT_CLASS[tone])} aria-hidden />
                {intl.formatMessage(effectFamilyMessages[tone])}
              </li>
            ))}
          </ul>
        )}
        <span className={styles.grow} />
        <div className={styles.zoom}>
          <button
            type="button"
            className={styles.zoomButton}
            aria-label={intl.formatMessage(messages.zoomOut)}
            title={intl.formatMessage(messages.zoomOut)}
            disabled={zoomIndex <= 0}
            onClick={() => setZoom(STRIP_ZOOMS[Math.max(0, zoomIndex - 1)] ?? 1)}
          >
            −
          </button>
          <span className={styles.zoomLevel} aria-live="polite">
            {intl.formatMessage(messages.zoomLevel, { zoom })}
          </span>
          <button
            type="button"
            className={styles.zoomButton}
            aria-label={intl.formatMessage(messages.zoomIn)}
            title={intl.formatMessage(messages.zoomIn)}
            disabled={zoomIndex >= STRIP_ZOOMS.length - 1}
            onClick={() => setZoom(STRIP_ZOOMS[Math.min(STRIP_ZOOMS.length - 1, zoomIndex + 1)] ?? 1)}
          >
            +
          </button>
        </div>
      </div>
      <div className={styles.stripBody}>
        <div className={styles.laneNames} aria-hidden>
          <span className={clsx(styles.laneName, styles.laneSections)}>
            {intl.formatMessage(messages.laneSections)}
          </span>
          <span className={clsx(styles.laneName, styles.laneShots)}>{intl.formatMessage(messages.laneShots)}</span>
          {tones.length > 0 && (
            <span className={styles.laneName} style={{ height: `${tones.length * 16 - 6}px` }}>
              {intl.formatMessage(messages.laneEffects)}
            </span>
          )}
          <span className={clsx(styles.laneName, styles.laneSong)}>{intl.formatMessage(messages.laneSong)}</span>
        </div>
        <div className={styles.scroller} ref={scrollerRef}>
          <div className={styles.lanes} style={{ width: `${trackPx}px` }}>
            <div className={clsx(styles.track, styles.trackSections)}>
              {sections.map((section, index) => {
                const item = sectionItems[index];
                const cell = stripCell(section.startSec, section.endSec, span, trackPx);
                const name = sectionDisplayName(section.label);
                return (
                  <div
                    key={section.index}
                    className={clsx(styles.sectionBox, SECTION_CLASS[item?.tone ?? 'verse'])}
                    style={px(cell)}
                    title={tip(name, section.startSec, section.endSec)}
                  >
                    {sectionCellLabel(name, item?.short ?? '', cell.width)}
                  </div>
                );
              })}
            </div>
            <div className={clsx(styles.track, styles.trackShots)}>
              {script.shots.map((shot, index) => {
                const cell = stripCell(shot.startSec, shot.endSec, span, trackPx);
                const label = intl.formatMessage(messages.shotBox, {
                  number: index + 1,
                  from: formatTime(shot.startSec),
                  to: formatTime(shot.endSec),
                });
                return (
                  <button
                    key={shot.id}
                    type="button"
                    className={clsx(styles.shotBox, shot.id === selectedShotId && styles.shotBoxOn)}
                    style={px(cell)}
                    aria-label={label}
                    title={label}
                    onClick={() => onShot(shot.id)}
                  >
                    {shotCellLabel(index + 1, cell.width)}
                  </button>
                );
              })}
            </div>
            {tones.map((tone) => (
              <div key={tone} className={clsx(styles.track, styles.trackEffects)}>
                {script.effects
                  .filter((effect) => effectToneOf(effect) === tone)
                  .map((effect) => (
                    <div
                      key={effect.id}
                      className={clsx(styles.fxBox, EFFECT_CLASS[tone])}
                      style={px(stripEffectMark(effect.startSec, effect.endSec, span, trackPx))}
                      title={tip(intl.formatMessage(effectKindMessages[effect.kind]), effect.startSec, effect.endSec)}
                    />
                  ))}
              </div>
            ))}
            <div className={clsx(styles.track, styles.trackSong)} aria-hidden>
              {wave.map((bar) => (
                <i key={bar.key} className={styles.wave} style={bar.style} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const ScriptTimeline = TypedMemo(ScriptTimelineComponent);
